/* eslint jsx-a11y/no-static-element-interactions: 0 */
/**
 * @module project/components/participate_form
 */
import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import idx from 'idx';
import moment from 'moment';
import { PARTICIPATE_FORM_STEPS, PARTICIPATE_FORM_FLOW, BASE_64_REGEXP } from 'common/variables';
import ParticipateRevisionStep from 'project/containers/participate_revision_step';
import ParticipateUploadStep from 'project/components/participate_form/upload_step';
import ParticipateInfoStep from 'project/components/participate_form/info_step';
import ParticipateIntroStep from 'project/components/participate_form/intro_step';
import ParticipateCompleteStep from 'project/components/participate_form/complete_step';
import SwitchToDesktopStep from 'project/components/participate_form/switch_to_desktop';

const INITIAL_STATE = {
  agreed: false,
  entry: {
    name: '',
    description: '',
    file: '',
    thumbnail: '',
    licensing: {
      type: null,
      assetTypes: {},
    },
  },
  isRevision: false,
  submissionError: '',
  revision: {},
};

/**
 * Form to add a new project entry
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class ParticipateForm extends Component {
  constructor(props) {
    super(props);

    this.state = INITIAL_STATE;

    this.setFile = this.setFile.bind(this);
    this.selectEntry = this.selectEntry.bind(this);
    this.changeEntryImage = this.changeEntryImage.bind(this);
    this.submitParticipateForm = this.submitParticipateForm.bind(this);
    this.closeForm = this.closeForm.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { formOpen, fetchProjectEntries, user, project } = this.props;

    if (!prevProps.formOpen && formOpen) {
      fetchProjectEntries(project.id, undefined, undefined, user.username);
    }
  }

  setFile(fileData, thumbnail) {
    const entry = this.state.entry;
    const file = fileData.replace(BASE_64_REGEXP, '');
    entry.file = file;
    entry.thumbnail = thumbnail;
    this.setState({ entry });
  }

  getSubmissionError(err, isRevision) {
    let errString = `Failed to submit ${isRevision ? 'revision' : 'entry'}. Please try again.`;

    /* error related to naming submissions */
    const nameError = idx(err, _ => _.data.name[0]);
    if (nameError) {
      errString = nameError;
    }

    // @TODO: provide better error message for other scenarios
    return errString;
  }

  selectEntry(entryId, revision) {
    if (entryId) {
      this.setState({
        isRevision: true,
        revision: {
          thumbnail: revision.thumbnails[0],
          entryId,
        }
      });
    } else {
      this.setState({
        isRevision: false,
        revision: {},
      });
    }
  }

  changeEntryImage(event, revision) {
    event.preventDefault();
    if (revision) {
      this.props.changeStep(PARTICIPATE_FORM_STEPS.revision);
    } else {
      this.props.changeStep(PARTICIPATE_FORM_STEPS.upload);
    }
  }

  resetState() {
    this.props.changeStep(PARTICIPATE_FORM_STEPS.intro);
    this.setState({ ...INITIAL_STATE });
  }

  closeForm() {
    this.resetState();
    this.props.toggleForm();
  }

  submitParticipateForm(entryData) {
    const { project } = this.props;
    const projectId = project.id;
    const stateEntry = this.state.entry;
    const entryId = this.state.revision.entryId;
    let formData = null;

    /* image entry type */
    if (project.entry_type === 'image') {
      formData = {
        image: stateEntry.file,
        is_entirely_original: (entryData.licensing.type === 'original'),
        additional_costs: entryData.licensing.assetValues,
        description: entryData.description,
      };

    /* name entry type */
    } else {
      formData = {
        is_entirely_original: true,  // it can't be otherwise...
        ...entryData
      };
    }

    /* clear submission error before submitting */
    this.setState({ submissionError: '', submitting: true });

    /* create new revision for existing entry */
    if (this.state.isRevision) {
      this.props.createEntryRevision(projectId, entryId, formData, () => {
        this.props.changeStep(PARTICIPATE_FORM_STEPS.complete);
        this.setState({ submitting: false });
      }, err => (
          this.setState({ submissionError: this.getSubmissionError(err), submitting: false })
        )
      );
    /* create new entry AND revision associated to it */
    } else {
      this.props.createProjectEntry({ project: projectId },
        formData, () => {
          this.props.changeStep(PARTICIPATE_FORM_STEPS.complete);
          this.setState({ submitting: false });
        }, err => (
          this.setState({ submissionError: this.getSubmissionError(err), submitting: false })
        )
      );
    }
  }

  renderParticipateForm() {
    const {
      projectSlug, project, subCategory, toggleForm, canWatch, user, currentStep
    } = this.props;

    const formClass = classNames('form-participate',
      (`step${PARTICIPATE_FORM_FLOW.indexOf(currentStep)}` || ''), {
        design: project.entry_type === 'image',
      }
    );

    const forceDesktop = project.entry_type === 'image';

    /* FIXME: use common <ModalDialog/> */
    return (<div className="modal-dialog">
      <div className="modal-background" onClick={this.closeForm} />
      <div className="modal-content-container wide">
        {forceDesktop && <div className="show-for-small-only">
          <SwitchToDesktopStep onCloseForm={toggleForm} canWatch={canWatch} />
        </div>}
        <div className={forceDesktop ? 'show-for-medium' : ''}>
          <form className={formClass}>
            {currentStep === PARTICIPATE_FORM_STEPS.intro && <ParticipateIntroStep
              project={project}
              currentStep={currentStep}
              entryType={project.entry_type}
              agreed={this.state.agreed}
              projectSlug={projectSlug}
              onCloseForm={this.closeForm}
            />}
            { currentStep === PARTICIPATE_FORM_STEPS.revision &&
              <ParticipateRevisionStep
                user={user}
                currentStep={currentStep}
                onSelectEntry={this.selectEntry}
                onCloseForm={this.closeForm}
              />
            }
            { currentStep === PARTICIPATE_FORM_STEPS.upload &&
              <ParticipateUploadStep
                currentStep={currentStep}
                onCrop={this.setFile}
                isRevision={this.state.isRevision}
                revision={this.state.revision}
                onChangeEntry={this.changeEntryImage}
                onCloseForm={this.closeForm}
              />
            }
            { currentStep === PARTICIPATE_FORM_STEPS.info &&
              <ParticipateInfoStep
                project={project}
                currentStep={currentStep}
                entry={this.state.entry}
                isRevision={this.state.isRevision}
                revision={this.state.revision}
                entryType={project.entry_type}
                onChangeEntry={this.changeEntryImage}
                onCloseForm={this.closeForm}
                onSubmitParticipateForm={this.submitParticipateForm}
                submissionError={this.state.submissionError}
                submittingForm={this.state.submitting}
              />
            }
            { currentStep === PARTICIPATE_FORM_STEPS.complete &&
              <ParticipateCompleteStep
                currentStep={currentStep}
                thumbnail={this.state.entry.thumbnail}
                entryType={project.entry_type}
                project={project}
                subCategory={subCategory}
                onCloseForm={this.closeForm}
              />
            }
          </form>
        </div>
      </div>
    </div>);
  }

  render() {
    return (this.props.formOpen ? this.renderParticipateForm() : null);
  }
}

/**
 * PropTypes.
 *
 * @property {string} projectSlug - slug for the project that is displayed
 * @property {string} currentStep - current step of the form
 * @property {bool} formOpen - variable controlling if form is visible
 * @property {object} project - project data object
 * @property {object} subCategory - project's sub-category object
 * @property {func} toggleForm - action creator to toggle form
 * @property {func} changeStep - action creator to change form step
 * @property {func} createProjectEntry - action creator to create a new entry
 * @property {func} createEntryRevision - action creator to create a new revision
 * @property {bool} canWatch - whether logged in user can watch the project
 */
ParticipateForm.propTypes = {
  projectSlug: PropTypes.string,
  currentStep: PropTypes.string,
  formOpen: PropTypes.bool,
  project: PropTypes.object,
  subCategory: PropTypes.object,
  toggleForm: PropTypes.func,
  changeStep: PropTypes.func,
  createProjectEntry: PropTypes.func,
  createEntryRevision: PropTypes.func,
  canWatch: PropTypes.bool,
  user: PropTypes.object,
};



// WEBPACK FOOTER //
// ./project/components/participate_form.jsx